import React, {useState, useEffect} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    getAuth,
    signInWithEmailAndPassword,
    browserLocalPersistence, signOut,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

import './LoginPage.css';
import {Button, Modal, Form, Alert} from "react-bootstrap";
import {Helmet} from "react-helmet-async";
import {get, getDatabase, ref} from "firebase/database";

const LoginPage = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(null);
    
    // Password reset modal states
    const [showResetModal, setShowResetModal] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [resetError, setResetError] = useState(null);
    const [resetSuccess, setResetSuccess] = useState(false);
    const [sending, setSending] = useState(false);

    // Login page customization states
    const [backgroundImageUrl, setBackgroundImageUrl] = useState(null);
    const [customerLogoUrl, setCustomerLogoUrl] = useState(null);
    const [configLoading, setConfigLoading] = useState(true);

    const auth = getAuth();
    const from = location.state?.from?.pathname || '/';

    // Load public login page configuration
    useEffect(() => {
        const loadLoginPageConfig = async () => {
            try {
                const db = getDatabase();
                const configRef = ref(db, 'publicSettings/loginPage');
                const snapshot = await get(configRef);
                
                if (snapshot.exists()) {
                    const config = snapshot.val();
                    setBackgroundImageUrl(config.backgroundImageUrl || null);
                    setCustomerLogoUrl(config.customerLogoUrl || null);
                }
            } catch (error) {
                console.error('Error loading login page config:', error);
                // Fail silently - use defaults
            } finally {
                setConfigLoading(false);
            }
        };

        loadLoginPageConfig();
    }, []);

    const handleLogin = async (e) => {
        e.preventDefault();
        setError(null);

        try {
            // 1) Sign in
            await auth.setPersistence(browserLocalPersistence);
            const { user } = await signInWithEmailAndPassword(auth, email, password);

            // 2) Check their "accessToWeb" flag
            const db = getDatabase();
            const accessSnap = await get(ref(db, `users/userList/${user.uid}/accessToWeb`));
            console.log(accessSnap.val());
            if (accessSnap.exists() && accessSnap.val() === true) {
                // 3a) Allowed → route them in
                navigate(from, { replace: true });
            } else {
                // 3b) Denied → sign out immediately, show error
                await signOut(auth);
                setError('Ditt konto har inte behörighet att använda webbverktygen.');
            }
        } catch (err) {
            console.error(err);
            setError('Inloggningen misslyckades eller ett fel uppstod vid hämtning av data.');
        }
    };

    const handleForgotPassword = () => {
        // Pre-fill with current email if available
        setResetEmail(email);
        setResetError(null);
        setResetSuccess(false);
        setShowResetModal(true);
    };

    const handleCloseResetModal = () => {
        setShowResetModal(false);
        setResetEmail('');
        setResetError(null);
        setResetSuccess(false);
        setSending(false);
    };

    const handleSendResetEmail = async () => {
        if (!resetEmail) {
            setResetError('Vänligen ange din e-postadress.');
            return;
        }

        setSending(true);
        setResetError(null);

        try {
            // Call the custom Firebase Function
            const sendPasswordResetEmailFunction = httpsCallable(functions, 'sendPasswordResetEmail');
            const result = await sendPasswordResetEmailFunction({ email: resetEmail });
            
            // The function always returns success (for security reasons)
            setResetSuccess(true);
        } catch (err) {
            console.error('Password reset error:', err);
            setResetError('Ett fel uppstod vid skickande av återställningsmail. Försök igen.');
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="login-page">

            <Helmet>
                <title>Molnfastigheter</title>
            </Helmet>

            <div className="login-left">
                <div className="logo-section">
                    <img
                        src="/Images/molnfastigheter_logo_white.png"
                        alt="Molnfastigheter"
                        className="logo-image"
                    />
                </div>

                <form onSubmit={handleLogin} className="login-form">
                    {error && <div className="error-message">{error}</div>}

                    <span className={"field-title"}>
                        E-postadress
                    </span>
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => {
                            setEmail(e.target.value);
                            setError(null);
                        }}
                        required
                        className="input-field"
                    />

                    <span className={"field-title"}>
                        Lösenord
                    </span>
                    <input
                        type="password"
                        value={password}
                        onChange={(e) => {
                            setPassword(e.target.value);
                            setError(null);
                        }}
                        required
                        className="input-field"
                    />

                    <Button
                        variant={"outline-light"}
                        className="mx-auto block mt-5 text-sm"
                        style={{borderWidth: '2px', width: '120px'}}
                        type={"submit"}
                    >
                        Logga in
                    </Button>

                    <button
                        type="button"
                        onClick={handleForgotPassword}
                        className="forgot-password-button"
                        style={{
                            marginTop: '20px'
                        }}
                    >
                        Glömt lösenord?
                    </button>
                </form>
            </div>

            <div className="login-right"
                 style={{
                     backgroundImage: backgroundImageUrl ? `url('${backgroundImageUrl}')` : 'none',
                     backgroundColor: backgroundImageUrl ? 'transparent' : 'white',
                     position: 'relative',
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center'
                 }}>
                {!configLoading && !backgroundImageUrl && (
                    <div style={{
                        color: '#999',
                        fontSize: '16px',
                        textAlign: 'center',
                        userSelect: 'none'
                    }}>
                        Ingen bakgrundsbild vald
                    </div>
                )}
                {customerLogoUrl && (
                    <div
                        style={{
                            position: 'absolute',
                            bottom: '26px',
                            right: '26px',
                            backgroundColor: 'white',
                            padding: '12px 20px',
                            borderRadius: '8px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                    >
                        <img
                            src={customerLogoUrl}
                            alt="Logo"
                            style={{
                                maxWidth: '200px',
                                maxHeight: '100px',
                                height: 'auto',
                                width: 'auto',
                                display: 'block'
                            }}
                        />
                    </div>
                )}
            </div>

            {/* Password Reset Modal */}
            <Modal show={showResetModal} onHide={handleCloseResetModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Återställ lösenord</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {!resetSuccess ? (
                        <>
                            <p>Ange din e-postadress så skickar vi instruktioner för att återställa ditt lösenord.</p>
                            
                            <Form.Group className="mb-3">
                                <Form.Label>E-postadress</Form.Label>
                                <Form.Control
                                    type="email"
                                    value={resetEmail}
                                    onChange={(e) => setResetEmail(e.target.value)}
                                    placeholder="Ange din e-postadress"
                                    disabled={sending}
                                />
                            </Form.Group>

                            {resetError && (
                                <Alert variant="danger" className="mb-3">
                                    {resetError}
                                </Alert>
                            )}
                        </>
                    ) : (
                        <div className="text-center">
                            <Alert variant="success" className="mb-3">
                                <strong>Ett mail med instruktioner på hur du återställer ditt lösenord har skickats.</strong>
                                <br />
                                <small>Om du inte får mailet, titta i skräpfiltret.</small>
                            </Alert>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {!resetSuccess ? (
                        <>
                            <Button variant="secondary" onClick={handleCloseResetModal} disabled={sending}>
                                Avbryt
                            </Button>
                            <Button 
                                variant="primary" 
                                onClick={handleSendResetEmail}
                                disabled={sending || !resetEmail}
                            >
                                {sending ? 'Skickar...' : 'Skicka återställningsmail'}
                            </Button>
                        </>
                    ) : (
                        <Button variant="primary" onClick={handleCloseResetModal}>
                            Stäng
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>

        </div>
    );
};

export default LoginPage;
